/**
 * 홈·추천 클래스처럼 ISR 로 그리는 화면의 쿼리(키 + 조회 함수)와 서버 프리페치.
 *
 * 서버 프리페치(page.tsx)와 클라이언트 훅(services/query)이 같은 키를 써야 하이드레이션이 맞물린다.
 * 예전엔 이 화면들의 후기·지점·요금·배너를 브라우저에서만 받아, 스크립트를 돌리지 않는 수집기(네이버 등)가
 * 본문의 80% 가량을 못 읽었다(2026-10-19 점검).
 */
import { ApiResponse, QUERY_KEYS } from '@/services/base-api';
import { getBannerList } from '@/services/banner.service';
import { getPricingBestList } from '@/services/pricing.service';
import { getFeaturedStoreReviewList } from '@/services/store-review.service';
import { getHotStoreList, getStoreList } from '@/services/store.service';
import {
  BannerPosition,
  GetBannerListParams,
} from '@/services/types/banner.type';
import { GetStoreListParams, StoreType } from '@/services/types/store.type';
import { QueryClient, QueryFunction, QueryKey } from '@tanstack/react-query';

/**
 * 서버 HTML 에 실려 오는 쿼리의 브라우저 쪽 신선도.
 *
 * 앱 기본값이 refetchOnMount: false 라, ISR(최대 하루) 로 구운 데이터를 그대로 보여 주면 가격·후기가
 * 하루 묵을 수 있다. 그래서 받은 지 5분이 지난 데이터는 화면이 뜰 때 다시 받는다 — 수집기는 HTML 로
 * 본문을 읽고, 사람은 예전처럼 최신 값을 본다.
 */
export const HYDRATED_QUERY_OPTIONS = {
  staleTime: 5 * 60 * 1000,
  refetchOnMount: true,
} as const;

type PageQuery<T = unknown> = {
  queryKey: QueryKey;
  queryFn: QueryFunction<ApiResponse<T>>;
};

export function bannerListQuery(params?: GetBannerListParams) {
  return {
    queryKey: [QUERY_KEYS.BANNER, QUERY_KEYS.LIST, params],
    queryFn: () => getBannerList(params),
  } satisfies PageQuery;
}

export function featuredStoreReviewListQuery() {
  return {
    queryKey: [QUERY_KEYS.STORE_REVIEW, 'featured'],
    queryFn: getFeaturedStoreReviewList,
  } satisfies PageQuery;
}

export function hotStoreListQuery() {
  return {
    queryKey: [QUERY_KEYS.STORE, QUERY_KEYS.LIST, 'hot'],
    queryFn: () => getHotStoreList(),
  } satisfies PageQuery;
}

export function storeListQuery(params?: GetStoreListParams) {
  return {
    queryKey: [QUERY_KEYS.STORE, QUERY_KEYS.LIST, params],
    queryFn: () => getStoreList(params),
  } satisfies PageQuery;
}

/** 홈 요금 영역의 대표 요금(시간제 등) */
export function pricingBestListQuery() {
  return {
    queryKey: [QUERY_KEYS.PRICING, QUERY_KEYS.LIST, 'best'],
    queryFn: getPricingBestList,
  } satisfies PageQuery;
}

/** 추천 클래스 하단 지점 목록. 직영점만 보이는 화면(무제한)과 전체 화면(장롱·주차)이 있다 */
export const RECOMMEND_CLASS_STORE_PARAMS = {
  direct: { page: 1, limit: 100, types: StoreType.DIRECT },
  all: { page: 1, limit: 100 },
} satisfies Record<string, GetStoreListParams>;

/**
 * 쿼리들을 서버 쿼리 캐시에 넣는다. 하나가 실패해도 나머지는 싣고, 실패한 것은 비워 둬
 * 클라이언트가 다시 받는다(prefetchQuery 는 오류를 던지지 않는다).
 */
export async function prefetchPageQueries(
  queryClient: QueryClient,
  queries: PageQuery[],
) {
  await Promise.all(queries.map((query) => queryClient.prefetchQuery(query)));
}

/** 홈 화면 쿼리 */
export function prefetchHomeQueries(queryClient: QueryClient) {
  return prefetchPageQueries(queryClient, [
    bannerListQuery({ position: BannerPosition.TOP }),
    bannerListQuery({ position: BannerPosition.MIDDLE }),
    featuredStoreReviewListQuery(),
    hotStoreListQuery(),
    pricingBestListQuery(),
  ]);
}
